import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CardContent, Modal } from '@mui/material';
import MainCard from 'components/MainCard';
import { modals } from 'components/modals/Modals';
import useModals from 'components/modals/useModal';
import { getEquipmentsByZone } from 'components/axios/equipmentAxios';
import { getEquipmentLocationMapByMapId, patchEquipmentLocationMapCoordinates } from 'components/axios/equipmentZoneAxios';
import { markerColorByStatus } from 'data/data';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

const TransparentTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    padding: 0,
  },
}));

export function CompanyImageEquipmentMappingLayer({fileUrl, mapsId, zoneId}) {
  const { t } = useTranslation();
  
  // modal 관련
  const { openModal } = useModals();
  const handleAlertClick = (alertMessage) => {
      openModal(modals.alertModal, {
          onClose : () => {
              // console.log(1111);
          },
          message: alertMessage,
      });
  };

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  }
  const handleClose = () => setOpen(false);
  
  const [equipmentList, setEquipmentList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [markers, setMarkers] = useState([]); // marker infos(id, position)
  const [equipmentMapping, setEquipmentMapping] = useState({}); // equipment-marker mapping infos(markerId, equipmentName)
  const [selectedMarkerId, setSelectedMarkerId] = useState(null); // 선택된 markerId
  const [selectedCoords, setSelectedCoords] = useState(null); //  선택된 position

  const [isLoading, setIsLoading] = useState(false);
  const [equipmentMappingList, setEquipmentMappingList] = useState(null);
  const imageRef = useRef(null);
  // 1. 이미지 원본 비율 저장할 state 추가
  const [aspectRatio, setAspectRatio] = useState(null);

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth && naturalHeight) {
      setAspectRatio(naturalWidth / naturalHeight);
    }
  };

  useEffect(()=> {
    if (open) {
      Promise.all([
        getEquipmentsByZone(zoneId).then((res) => {
          // console.log(res)
          setEquipmentList(res)
        })
      ]).then(()=>{
        setIsLoading(true)
      })
    }
  },[open])

  useEffect(()=>{
    if (open && !isLoading) {
      getEquipmentLocationMapByMapId(mapsId).then((res) => {
        if (res && res.coordinates) {
          const newMapping = {};
          const newMarkers = res.coordinates.map((m) => {
            const markerId = `marker-${Math.floor(Math.random() * 10000)}`;
            
            // 기존 매핑 정보 복원
            if (m.equipment) {
              newMapping[markerId] = `${m.equipment.name}-${m.equipment.id}`;
            }

            return {
              id: markerId,
              equipment: m.equipment, // 툴팁 표시에 필수
              position: { x: m.yawOrX, y: m.pitchOrY },
              status: m.equipment?.status || 'unmapped'
            };
          });

          setEquipmentMapping(newMapping);
          setMarkers(newMarkers);
          setEquipmentMappingList(res.coordinates);
        } else {
          setEquipmentMappingList([]);
          setEquipmentMapping({});
          setMarkers([]);
        }
      });
    }
  }, [open, isLoading])

  const handleImageClick = (e) => {
    if (!imageRef.current) return;

    if (e.target !== imageRef.current) return;

    // const rect = e.target.getBoundingClientRect();
    // const x = e.clientX - rect.left - 10;
    // const y = e.clientY - rect.top - 10;
    const rect = imageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    const id = `marker-${Date.now()}`;
    setMarkers((prev) => [
      ...prev,
      { id, position:{x, y}, status: "unmapped" },
    ]);
    setSelectedMarkerId(id);
    setSelectedCoords({ x, y });
  };

  const getAvailableEquipments = () => {
    const used = Object.values(equipmentMapping);
    return equipmentList.filter(e => !used.includes(e.name+'-'+e.id));
  };

  // 마커 클릭 (싱글) → 모달 열기
  const handleMarkerClick = (data) => {
    // console.log(data)
    // 이미 매핑된 마커가 아니고 + 남은 설비도 없을 때만 클릭 막기
    const isAlreadyMapped = !!equipmentMapping[data.id];
    if (!isAlreadyMapped && getAvailableEquipments().length === 0) {
      handleAlertClick(t('message.common.alert.all.equipment.mapping.done'))
      return;
    }
    setSelectedMarkerId(data.id);
    setSelectedCoords({ x: data.position.x, y: data.position.y });
    setModalOpen(true);
  };

  // 마커 더블클릭 → 삭제
  const handleMarkerDoubleClick = (markerId) => {
    if (!markerId) return;
    
    // 상태에서 제거
    setMarkers(prev => prev.filter(m => m.id !== markerId));
    
    // 매핑정보 제거
    const newMapping = { ...equipmentMapping };
    delete newMapping[markerId];
    setEquipmentMapping(newMapping);

    console.log(`Marker ${markerId} deleted`);
    setModalOpen(false);
    // setMarkers((prev) => prev.filter((m) => m.id !== id));
    // setSelectedMarker(null);
  };

  // 설비 매핑
  const handleMap = async (equipmentId) => {
    if (!selectedMarkerId) return;
    // console.log(equipmentList)
    const equipment = equipmentList?.find(e => e.id == equipmentId);
    // console.log(equipment)
    // const image = markerColorByStatus[equipment.status];
    const marker = markers.find(m => m.id === selectedMarkerId);
    if (!marker || !equipment) return;

    const usedIds = Object.values(equipmentMapping).map(v => v.split("-").pop());
    // if (equipmentMapping.some(m => m.split("-").pop() == equipmentId)) {
    if (usedIds.includes(String(equipmentId))) {
      handleAlertClick(t('message.common.alert.already.mapping.equipment.retry.select'))
      return;
    }

    const newMapping = {
      ...equipmentMapping,
      [selectedMarkerId]: `${equipment.name}-${equipmentId}`,
    };
    setEquipmentMapping(newMapping);
    // console.log('Map Marker')

    setMarkers((prev) =>
      prev.map((m) =>
        m.id === selectedMarkerId
          ? { ...m, equipment: equipment, status: equipment.status }
          : m
      )
    );
    // console.log(markers);
    // setSelectedMarker(null);
    setModalOpen(false);
  };

  const handleMapEquipmentSave = () => {
    let locationMaps = [];
    if (equipmentMapping && markers) {
      markers.map((m)=>{
        console.log(m)
        const markerName = equipmentMapping[m.id]
        if (markerName) {
          const equipment = equipmentList.find(e => e.id == markerName.substring(markerName.lastIndexOf('-')+1));
          locationMaps.push({equipment:{id:equipment.id},yawOrX:m.position.x, pitchOrY:m.position.y})
        }
      })

      const body = {
        // dto: {
        id : mapsId,
        coordinates : locationMaps
        // }
      }
      console.log(body)

      openModal(modals.confirmModal, {
          onSubmit : (value) => {
              // console.log('locationMaps write')
              patchEquipmentLocationMapCoordinates(body.id, body).then(()=>{
                  handleAlertClick(t('message.common.alert.save.done'));
                  setIsLoading(false);
              }).catch((err)=>{
                  console.log(err);
                  handleAlertClick(err.response?.data?.message || err.message);
              });
          },
          onClose : () => {
          },
          message: t('message.common.alert.save.question'),
      });
    }
  }

  return (
      <>
        <img
          src={fileUrl}
          alt="Thumbnail"
          // className="w-32 h-32 object-cover cursor-pointer rounded"
          onClick={handleOpen} 
          style={{width: "80px" ,height: "80px" ,padding:"3px", cursor:'pointer'}}
        />
  
        <Modal className="pop_box" open={open} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
          <MainCard style={{maxWidth:"1440px", width:"90%", maxHeight:"80vh"}} title="" modal darkTitle content={false}>
            <CardContent style={{height:"100%"}}>
              <div className='pop_close'>
                <Button color="primary" size="small" onClick={handleClose}>
                  {t('message.common.btn.close')}
                </Button>
              </div>
  
              {/* 2. 스크롤을 방지하고 이미지를 부모 영역에 맞추는 컨테이너 */}
              <div style={{
                width: "100%", 
                height: '55vh', 
                overflow: "hidden", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center"
              }}>
                {/* 3. 이미지 원본 비율로 구속되는 마커 감싸기 래퍼 */}
                <div
                  style={{ 
                    position: "relative", 
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    maxWidth: '100%', 
                    maxHeight: '100%',
                    aspectRatio: aspectRatio ? `${aspectRatio}` : "auto"
                  }}
                  onClick={handleImageClick}
                >
                  <img
                    // src={`https://photo-sphere-viewer-data.netlify.app/assets/sphere.jpg`}
                    ref={imageRef}
                    src={fileUrl}
                    alt="factory"
                    onLoad={handleImageLoad}
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      objectFit: "contain"
                    }}
                  />
                  {markers?.map((m, mIdx) => {
                    // console.log(m)
                    return (
                      <TransparentTooltip key={mIdx}
                        title={
                          <React.Fragment>
                            <div style={{width:"250px",
                              background: "rgba(0,0,0,0.75)",
                              padding: "12px 16px",
                              borderRadius: "10px",
                              color: "#fff",
                              fontSize: "14px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"}}>
                              {m.equipment ? `${m.equipment.name} (${m.equipment.equipmentNo})` : t('message.common.text.Unmapped')}
                            </div>
                          </React.Fragment>
                        }
                        // arrow
                        placement="top"
                      >
                    <img
                      key={m.id}
                      src={markerColorByStatus[m.status]}
                      style={{
                        position: "absolute",
                        top: `${m.position?.y * 100}%`,
                        left: `${m.position?.x * 100}%`,
                        width: 32,
                        height: 32,
                        cursor: "pointer",
                        zIndex: "1000",
                        transform: "translate(-50%, -50%)"
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // 이미지 클릭 이벤트 막기
                        handleMarkerClick(m);
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        handleMarkerDoubleClick(m.id);
                      }}
                    />
                    </TransparentTooltip>
                    )}
                  )}

                  {modalOpen && (
                  <div
                    style={{
                      position: "absolute",
                      left: `${Math.min((selectedCoords?.x || 0) * 100, 60)}%`,
                      top: `${Math.min((selectedCoords?.y || 0) * 100, 60)}%`,
                      width: "300px", 
                      background: "white",
                      color: "#000",
                      padding: "12px",
                      borderRadius: "8px",
                      boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
                      zIndex: "2000"
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <h3>{t('message.common.title.equipment.mapping')}</h3>
                    <p>
                      <b>{t('message.common.subtitle.position')}:</b> x : {selectedCoords?.x.toFixed(4)}, y : {selectedCoords?.y.toFixed(4)}
                      {equipmentMapping[selectedMarkerId] && <>, <b>{t('message.common.subtitle.equipment.name')}</b> : {equipmentMapping[selectedMarkerId].substring(0,equipmentMapping[selectedMarkerId].lastIndexOf('-'))}</>}
                    </p>

                    <div style={{display:'flex',justifyContent:'space-between', alignItems:'center', width:'100%'}}>
                      <div style={{witdh:'200px'}}>
                        <select
                          onChange={(e) =>
                            handleMap(e.target.value)
                          }
                          defaultValue=""
                        >
                          <option value="" disabled>
                            {t('message.common.select.equipment.select')}
                          </option>
                          {getAvailableEquipments().map((e) => (
                            <option key={e.id} value={e.id}>
                              {e.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{display:"flex", justifyContent: 'flex-end', alignItems:'center', gap:'16px', flex:'1'}}>
                        <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                          <button onClick={() => setModalOpen(false)} style={{padding:'5px', margin:'5px'}}>{t('message.common.btn.close')}</button>
                        </div>
                      
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>
              <div style={{float:'right'}}>
                <Button variant="contained" color="primary" size="small" onClick={handleMapEquipmentSave}>
                  {t('message.common.btn.save')}
                </Button>
              </div>
              <div>
                * {t('message.common.guide.equipment.image.mapping.title')}<br/>
                1. {t('message.common.guide.equipment.image.mapping.step1')}<br/>
                2. {t('message.common.guide.equipment.image.mapping.step2')}<br/>
                3. {t('message.common.guide.equipment.image.mapping.step3')}<br/>
                4. {t('message.common.guide.equipment.image.mapping.step4')}<br/>
                5. {t('message.common.guide.equipment.image.mapping.step5')}
              </div>
            </CardContent>
  
          </MainCard>
        </Modal>

      </>
  )
}

export default CompanyImageEquipmentMappingLayer;
  